import React from "react";
import { useTheme } from "../config/ThemeContext";

const ScoreBoard = ({ matchState, currentSet, handleScoreChange, shouldFlip = false }) => {
  const teamA = matchState?.teams?.teamA || {};
  const teamB = matchState?.teams?.teamB || {};
  const scoreA = matchState?.sets?.[currentSet]?.scoreA ?? 0;
  const scoreB = matchState?.sets?.[currentSet]?.scoreB ?? 0;
  const setsA = matchState?.setsWon?.teamA ?? 0;
  const setsB = matchState?.setsWon?.teamB ?? 0;
  const servingTeam = matchState?.sets?.[currentSet]?.servingTeam;
  const colors = useTheme();

  const isTeamAServing = Number(servingTeam) === teamA.id;
  const isTeamBServing = Number(servingTeam) === teamB.id;

  return (
    <div className="relative">
	
      {/* Main Scoreboard Container */}
      <div className={`
        relative overflow-hidden rounded-2xl shadow-2xl
        bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm
        border border-white/20
        ${shouldFlip ? "flex-row-reverse" : ""}
        flex items-stretch justify-between
        p-6 gap-1
        min-h-[200px]
      `}>
        
        {/* Team A Section */}
        <div className={`
          flex flex-col items-center justify-between
          w-1/3 min-w-[160px]
          transition-all duration-300 ease-out
          ${isTeamAServing ? 'scale-105' : 'scale-100'}
        `}>
          {/* Team Header */}
          <div className="text-center mb-4">
            <h2 className={`
              text-2xl font-bold mb-1
              ${colors.teamA.text}
              transition-colors duration-200
            `}>
              {teamA.code || "Team A"}
            </h2>
            <div className="text-sm text-gray-600 font-medium">
              Sets: {setsA}
            </div>
          </div>

          {/* Score Display */}
          <div className="relative mb-6">
            <div className={`
              text-7xl font-black leading-none
              ${colors.teamA.text}
              transition-all duration-300
              ${isTeamAServing ? 'animate-pulse' : ''}
            `}>
              {scoreA}
            </div>
          </div>

          {/* Score Controls */}
          <div className="flex gap-2">
            <button
              className="
                w-6 h-6 rounded-lg
                bg-gray-100 hover:bg-gray-200
                text-gray-600 font-medium text-sm
                border border-gray-300
                transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-gray-300
                active:scale-95
              "
              onClick={() => handleScoreChange("teamA", -1)}
              aria-label="Decrease Team A score"
            >
              −
            </button>
            <button
              className="
                w-6 h-6 rounded-lg
                bg-gray-100 hover:bg-gray-200
                text-gray-600 font-medium text-sm
                border border-gray-300
                transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-gray-300
                active:scale-95
              "
              onClick={() => handleScoreChange("teamA", 1)}
              aria-label="Increase Team A score"
            >
              +
            </button>
          </div>
        </div>

        {/* Center Section - Set Info & Match Status */}
        <div className="
          flex flex-col items-center justify-center
          w-1/3 min-w-[120px]
          relative
        ">
          {/* Set Display */}
          <div className="text-center mb-6">
            <div className="text-sm font-medium text-gray-500 mb-1">
              SET
            </div>
            <div className={`
              text-4xl font-black text-gray-700
            `}>
              {currentSet}
            </div>
          </div>

          {/* VS Separator */}
          <div className="
            text-2xl font-bold text-gray-400
            mb-4
          ">
            VS
          </div>
        </div>

        {/* Team B Section */}
        <div className={`
          flex flex-col items-center justify-between
          w-1/3 min-w-[160px]
          transition-all duration-300 ease-out
          ${isTeamBServing ? 'scale-105' : 'scale-100'}
        `}>
          {/* Team Header */}
          <div className="text-center mb-4">
            <h2 className={`
              text-2xl font-bold mb-1
              ${colors.teamB.text}
              transition-colors duration-200
            `}>
              {teamB.code || "Team B"}
            </h2>
            <div className="text-sm text-gray-600 font-medium">
              Sets: {setsB}
            </div>
          </div>

          {/* Score Display */}
          <div className="relative mb-6">
            <div className={`
              text-7xl font-black leading-none
              ${colors.teamB.text}
              transition-all duration-300
              ${isTeamBServing ? 'animate-pulse' : ''}
            `}>
              {scoreB}
            </div>
          </div>

          {/* Score Controls */}
          <div className="flex gap-2">
            <button
              className="
                w-6 h-6 rounded-lg
                bg-gray-100 hover:bg-gray-200
                text-gray-600 font-medium text-sm
                border border-gray-300
                transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-gray-300
                active:scale-95
              "
              onClick={() => handleScoreChange("teamB", -1)}
              aria-label="Decrease Team B score"
            >
              −
            </button>
            <button
              className="
                w-6 h-6 rounded-lg
                bg-gray-100 hover:bg-gray-200
                text-gray-600 font-medium text-sm
                border border-gray-300
                transition-all duration-200
                focus:outline-none focus:ring-2 focus:ring-gray-300
                active:scale-95
              "
              onClick={() => handleScoreChange("teamB", 1)}
              aria-label="Increase Team B score"
            >
              +
            </button>
          </div>
        </div>

        {/* Decorative Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-purple-50/30 pointer-events-none" />
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-30" />
      </div>
    </div>
  );
};

export default ScoreBoard;
